import {
  applyBrand,
  applyOps,
  diff,
  EMPTY_PRESENCE,
  LIVE_MAX_OPS_PER_BATCH,
  type LiveClientMessage,
  type LiveOp,
  type LivePeer,
  type LivePresence,
  type LiveServerMessage,
  type SceneDocument,
} from "@cva/shared";
import { createContext, useContext } from "react";
import { toast } from "sonner";
import { createStore, type StoreApi, useStore } from "zustand";
import { noteRateLimit, retryAfterMs } from "../lib/rate-limit";
import type { EditorStore } from "./store";

/**
 * Live collaboration in the browser (docs/17-live-kollaboration.md).
 *
 * - `confirmed`: the server's state (all changes it applied, in its order).
 * - `pending`: own changes sent but not yet applied by the server.
 * - The editor shows `confirmed` + `pending` + what was not sent yet. When
 *   someone else's change arrives, it goes into `confirmed` and the own
 *   changes are applied on top again ("rebase").
 *
 * Transport: server-sent events for everything from the server, one POST at a
 * time for everything to it.
 */

export type LiveStatus = "connecting" | "live" | "offline" | "closed";

export interface LiveState {
  status: LiveStatus;
  /** Own changes the server has not written to the database yet. */
  unsaved: boolean;
  access: "edit" | "view" | null;
  self: LivePeer | null;
  /** The others in the file, one entry per editor session. */
  peers: LivePeer[];
  closedReason: string | null;
}

interface Batch {
  seq: number;
  ops: LiveOp[];
  sent: boolean;
}

export interface LiveOptions {
  orgId: string;
  documentId: string;
  onMeta: (title: string | undefined) => void;
  onComments: () => void;
}

/** Changes during a pointer drag are sent this often (others see it move). */
const GESTURE_FLUSH_MS = 60;
const FLUSH_MS = 30;
/** At most one request this often (cursor movements are merged). */
const MIN_SEND_INTERVAL_MS = 50;
const MAX_MESSAGES_PER_REQUEST = 200;

function randomClientId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** The operations as the server will see them (JSON), so both sides apply the same. */
function onWire(ops: LiveOp[]): LiveOp[] {
  return JSON.parse(JSON.stringify(ops));
}

export class LiveSync {
  readonly clientId = randomClientId();
  readonly state: StoreApi<LiveState> = createStore<LiveState>(() => ({
    status: "connecting",
    unsaved: false,
    access: null,
    self: null,
    peers: [],
    closedReason: null,
  }));

  private confirmed: SceneDocument | null = null;
  /** The editor's document at the last flush or rebase: later edits are diffed against it. */
  private base: SceneDocument;
  /** Edits since the last flush (only measurements or brand values: nothing to send). */
  private edited = false;
  private pending: Batch[] = [];
  /** Applied by the server, not yet saved (sent again if the server restarts). */
  private unsaved: { seq: number; ops: LiveOp[]; rev: number }[] = [];
  private seq = 0;
  private epoch: string | null = null;
  private conn: string | null = null;
  private source: EventSource | null = null;
  private presence: LivePresence = EMPTY_PRESENCE;
  private presenceDirty = false;
  private saveRequested = false;
  /** Messages about AI participants (stop, take back), sent with the next request. */
  private extra: LiveClientMessage[] = [];
  private sending = false;
  private lastSendAt = 0;
  private sendTimer: ReturnType<typeof setTimeout> | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 1000;
  private rebaseWanted = false;
  private waiters: { until: "acked" | "saved"; resolve: () => void }[] = [];
  private unsubscribe: (() => void) | null = null;
  private stopped = false;
  private lastRejectToast = 0;

  constructor(
    private readonly store: EditorStore,
    private readonly options: LiveOptions,
  ) {
    this.base = store.getState().doc;
  }

  start() {
    this.stopped = false;
    this.unsubscribe = this.store.subscribe((state, previous) => {
      if (state.doc !== previous.doc && state.changeKind !== "remote") {
        if (state.changeKind === "edit") this.edited = true;
        this.scheduleFlush();
      }
      // A drag ended: show what the others changed meanwhile.
      if (previous.gestureBase && !state.gestureBase && this.rebaseWanted) {
        this.flush();
        this.rebase();
      }
      if (state.editingTextId && state.editingTextId !== previous.editingTextId) {
        this.yieldText(state.editingTextId, true);
      }
      const pageId = state.doc.pages[state.pageIndex]?.id ?? null;
      if (
        state.selection !== previous.selection ||
        state.editingTextId !== previous.editingTextId ||
        pageId !== this.presence.page
      ) {
        this.setPresence({
          page: pageId,
          selection: state.selection,
          editing: state.editingTextId,
        });
      }
    });
    const state = this.store.getState();
    this.presence = {
      ...EMPTY_PRESENCE,
      page: state.doc.pages[state.pageIndex]?.id ?? null,
    };
    window.addEventListener("pagehide", this.onPageHide);
    this.connect();
  }

  stop() {
    this.flush();
    this.sendRemaining();
    this.stopped = true;
    this.unsubscribe?.();
    window.removeEventListener("pagehide", this.onPageHide);
    this.source?.close();
    this.source = null;
    for (const timer of [this.sendTimer, this.flushTimer, this.reconnectTimer]) {
      if (timer) clearTimeout(timer);
    }
  }

  // -- public -------------------------------------------------------------------

  setPresence(update: Partial<LivePresence>) {
    const next = { ...this.presence, ...update };
    if (
      next.page === this.presence.page &&
      next.selection === this.presence.selection &&
      next.editing === this.presence.editing &&
      next.time === this.presence.time &&
      next.clip === this.presence.clip &&
      (next.cursor === this.presence.cursor ||
        (next.cursor !== null &&
          this.presence.cursor !== null &&
          next.cursor.x === this.presence.cursor.x &&
          next.cursor.y === this.presence.cursor.y))
    ) {
      return;
    }
    this.presence = next;
    this.presenceDirty = true;
    this.pump();
  }

  /** Stops (or releases again) the editing of an AI participant. */
  agentStop(client: string, on: boolean) {
    this.extra.push({ t: "agent-stop", client, on });
    this.pump();
  }

  /** Takes back the last package of changes of an AI participant. */
  agentUndo(client: string) {
    this.extra.push({ t: "agent-undo", client });
    this.pump();
  }

  /** Resolves once the server has applied all own changes (it then has the latest state). */
  whenApplied(): Promise<void> {
    this.flush();
    if (this.pending.length === 0) return Promise.resolve();
    return new Promise((resolve) => this.waiters.push({ until: "acked", resolve }));
  }

  /** Writes to the database now (⌘S) and resolves once it is saved. */
  save(): Promise<void> {
    this.flush();
    this.saveRequested = true;
    this.pump();
    if (this.pending.length === 0 && this.unsaved.length === 0) return Promise.resolve();
    return new Promise((resolve) => this.waiters.push({ until: "saved", resolve }));
  }

  /**
   * One person types in a text at a time: whoever else is in it keeps it
   * (when both started at once, the smaller session id).
   */
  private yieldText(elementId: string, starting: boolean) {
    const holder = this.state
      .getState()
      .peers.find(
        (peer) => peer.s.editing === elementId && (starting || peer.client < this.clientId),
      );
    if (!holder || this.store.getState().editingTextId !== elementId) return;
    this.store.getState().setEditingText(null);
    toast(`${holder.name} schreibt gerade in diesem Text.`, {
      description: "Du kannst ihn bearbeiten, sobald die Person fertig ist.",
    });
  }

  // -- local changes ------------------------------------------------------------

  private scheduleFlush() {
    if (this.flushTimer) return;
    const delay = this.store.getState().gestureBase ? GESTURE_FLUSH_MS : FLUSH_MS;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, delay);
  }

  /** Turns what changed in the editor since the last flush into a batch. */
  private flush() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    const { doc, readOnly } = this.store.getState();
    if (doc === this.base) return;
    if (!this.edited || readOnly || this.state.getState().access === "view") {
      // Measurements and brand values only: nothing to send.
      this.base = doc;
      this.edited = false;
      return;
    }
    const ops = onWire(diff(this.base, doc));
    this.base = doc;
    this.edited = false;
    for (let start = 0; start < ops.length; start += LIVE_MAX_OPS_PER_BATCH) {
      this.seq += 1;
      this.pending.push({
        seq: this.seq,
        ops: ops.slice(start, start + LIVE_MAX_OPS_PER_BATCH),
        sent: false,
      });
    }
    this.updateUnsaved();
    this.pump();
  }

  /** Rebase now, or after the running drag (it may recompute from the state before). */
  private rebaseSoon() {
    this.flush();
    const state = this.store.getState();
    if (state.gestureBase && !state.gestureLive) this.rebaseWanted = true;
    else this.rebase();
  }

  /** Shows `confirmed` + own pending changes in the editor. */
  private rebase() {
    if (!this.confirmed) return;
    this.rebaseWanted = false;
    let local = this.confirmed;
    for (const batch of this.pending) local = applyOps(local, batch.ops);
    const brand = this.store.getState().brand;
    if (brand) local = applyBrand(local, brand);
    this.store.getState().applyRemote(local);
    this.base = this.store.getState().doc;
  }

  // -- sending ------------------------------------------------------------------

  private pump() {
    if (this.sending || !this.conn || this.stopped) return;
    const hasOps = this.pending.some((batch) => !batch.sent);
    if (!hasOps && !this.presenceDirty && !this.saveRequested && this.extra.length === 0) return;
    const wait = MIN_SEND_INTERVAL_MS - (Date.now() - this.lastSendAt);
    if (wait > 0) {
      this.sendTimer ??= setTimeout(() => {
        this.sendTimer = null;
        this.pump();
      }, wait);
      return;
    }
    const messages: LiveClientMessage[] = [];
    const batches: Batch[] = [];
    for (const batch of this.pending) {
      if (batch.sent) continue;
      if (messages.length >= MAX_MESSAGES_PER_REQUEST - 2) break;
      batch.sent = true;
      batches.push(batch);
      messages.push({ t: "ops", seq: batch.seq, ops: batch.ops });
    }
    const presence = this.presenceDirty;
    if (presence) messages.push({ t: "presence", s: this.presence });
    const save = this.saveRequested;
    if (save) messages.push({ t: "save" });
    const extra = this.extra;
    this.extra = [];
    messages.push(...extra.slice(0, MAX_MESSAGES_PER_REQUEST - messages.length));
    this.presenceDirty = false;
    this.saveRequested = false;
    this.sending = true;
    this.lastSendAt = Date.now();
    const conn = this.conn;
    const retry = () => {
      for (const batch of batches) batch.sent = false;
      if (presence) this.presenceDirty = true;
      if (save) this.saveRequested = true;
      this.extra = [...extra, ...this.extra];
    };
    fetch("/api/live/send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ c: conn, m: messages }),
    })
      .then((response) => {
        if (response.ok) return;
        if (response.status === 410) {
          // The server forgot the connection: a new one sends everything again.
          retry();
          if (this.conn === conn) this.reconnect();
          return;
        }
        if (response.status === 429) {
          // Rate limit: keep the changes and send them once the server lets us.
          retry();
          const wait = retryAfterMs(response, 5000);
          this.lastSendAt = Date.now() + wait;
          noteRateLimit(
            "Sehr viele Änderungen in kurzer Zeit – sie werden gleich übertragen.",
            wait,
          );
          return;
        }
        if (response.status !== 400 && response.status !== 413) {
          // Server restarting, proxy error, session check: try again shortly.
          retry();
          this.lastSendAt = Date.now() + 1000;
          return;
        }
        // Not accepted at all (should not happen): drop these changes.
        this.pending = this.pending.filter((batch) => !batches.includes(batch));
        toast.error("Eine Änderung konnte nicht übertragen werden und wurde verworfen.");
        this.rebaseSoon();
        this.updateUnsaved();
        this.resolveWaiters();
      })
      .catch(() => {
        retry();
        this.lastSendAt = Date.now() + 1000;
      })
      .finally(() => {
        this.sending = false;
        this.pump();
      });
  }

  /** Leaving the page: send what is left, even though the page goes away. */
  private sendRemaining() {
    if (!this.conn) return;
    const messages: LiveClientMessage[] = this.pending
      .filter((batch) => !batch.sent)
      .map((batch) => ({ t: "ops", seq: batch.seq, ops: batch.ops }));
    if (messages.length === 0) return;
    try {
      fetch("/api/live/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ c: this.conn, m: [...messages, { t: "save" }] }),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // too large for keepalive: the server keeps what it already has
    }
  }

  private onPageHide = () => {
    this.flush();
    this.sendRemaining();
  };

  // -- receiving ----------------------------------------------------------------

  private connect() {
    if (this.stopped) return;
    const params = new URLSearchParams({
      org: this.options.orgId,
      doc: this.options.documentId,
      client: this.clientId,
    });
    const source = new EventSource(`/api/live/stream?${params}`, { withCredentials: true });
    this.source = source;
    source.onmessage = (event) => {
      try {
        this.receive(JSON.parse(event.data) as LiveServerMessage);
      } catch (error) {
        console.error("Live-Nachricht fehlerhaft", error);
      }
    };
    source.onerror = () => {
      if (this.source !== source) return;
      this.conn = null;
      if (this.state.getState().status !== "closed") this.state.setState({ status: "offline" });
      // The browser retries by itself unless the server refused (then it is closed).
      if (source.readyState === EventSource.CLOSED) this.reconnectLater();
    };
  }

  private reconnect() {
    this.source?.close();
    this.source = null;
    this.conn = null;
    this.connect();
  }

  private reconnectLater() {
    if (this.reconnectTimer || this.stopped) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnect();
    }, this.reconnectDelay);
    this.reconnectDelay = Math.min(30_000, this.reconnectDelay * 2);
  }

  private receive(message: LiveServerMessage) {
    switch (message.t) {
      case "init":
        this.onInit(message);
        return;
      case "ack": {
        const index = this.pending.findIndex((batch) => batch.seq === message.seq);
        if (index < 0 || !this.confirmed) return;
        // Batches are applied in order: everything before it was acknowledged already.
        const [batch] = this.pending.splice(index, 1);
        this.confirmed = applyOps(this.confirmed, batch!.ops);
        this.unsaved.push({ seq: batch!.seq, ops: batch!.ops, rev: message.rev });
        this.updateUnsaved();
        this.resolveWaiters();
        return;
      }
      case "reject": {
        const index = this.pending.findIndex((batch) => batch.seq === message.seq);
        if (index >= 0) this.pending.splice(index, 1);
        if (Date.now() - this.lastRejectToast > 5000) {
          this.lastRejectToast = Date.now();
          toast.warning(
            "Eine Änderung passte nicht zu den gleichzeitigen Änderungen der anderen und wurde zurückgenommen.",
            { description: message.reason },
          );
        }
        this.rebaseSoon();
        this.updateUnsaved();
        this.resolveWaiters();
        return;
      }
      case "ops": {
        if (!this.confirmed) return;
        this.flush();
        this.confirmed = applyOps(this.confirmed, message.ops);
        this.rebaseSoon();
        return;
      }
      case "saved":
        this.unsaved = this.unsaved.filter((entry) => entry.rev > message.rev);
        this.updateUnsaved();
        this.resolveWaiters();
        return;
      case "join":
        this.state.setState((state) => ({
          peers: [
            ...state.peers.filter((peer) => peer.client !== message.peer.client),
            message.peer,
          ],
        }));
        return;
      case "leave":
        this.state.setState((state) => ({
          peers: state.peers.filter((peer) => peer.client !== message.client),
        }));
        return;
      case "presence": {
        this.state.setState((state) => ({
          peers: state.peers.map((peer) =>
            peer.client === message.client ? { ...peer, s: message.s } : peer,
          ),
        }));
        const editing = this.store.getState().editingTextId;
        if (editing && message.s.editing === editing) this.yieldText(editing, false);
        return;
      }
      case "notice":
        toast.info(message.message);
        return;
      case "meta":
        this.options.onMeta(message.title);
        return;
      case "comments":
        this.options.onComments();
        return;
      case "closed":
        this.stopped = true;
        this.source?.close();
        this.source = null;
        this.conn = null;
        this.store.getState().setReadOnly(true);
        this.state.setState({ status: "closed", closedReason: message.reason, peers: [] });
        return;
    }
  }

  private onInit(message: Extract<LiveServerMessage, { t: "init" }>) {
    // Edits made while connecting count as pending changes.
    this.flush();
    if (message.epoch === this.epoch) {
      // Same room: batches up to `lastSeq` are in `doc` already.
      this.pending = this.pending.filter((batch) => batch.seq > message.lastSeq);
    } else if (this.epoch !== null) {
      // The server restarted: send again what it may not have saved.
      const lost = this.unsaved
        .filter((entry) => entry.rev > message.savedRev)
        .map((entry) => ({ seq: entry.seq, ops: entry.ops, sent: false }));
      this.pending = [...lost, ...this.pending];
      this.unsaved = [];
    }
    for (const batch of this.pending) batch.sent = false;
    this.epoch = message.epoch;
    this.conn = message.conn;
    this.confirmed = message.doc;
    this.reconnectDelay = 1000;
    if (message.access === "view") {
      // The server decides (the role may have changed since the page loaded).
      this.pending = [];
      this.store.getState().setReadOnly(true);
    }
    this.state.setState({
      status: "live",
      access: message.access,
      self: message.self,
      peers: message.peers,
    });
    this.rebase();
    this.updateUnsaved();
    this.presenceDirty = true;
    this.pump();
    this.resolveWaiters();
  }

  private updateUnsaved() {
    const unsaved = this.pending.length > 0 || this.unsaved.length > 0;
    if (this.state.getState().unsaved !== unsaved) this.state.setState({ unsaved });
  }

  private resolveWaiters() {
    const applied = this.pending.length === 0;
    const saved = applied && this.unsaved.length === 0;
    this.waiters = this.waiters.filter((waiter) => {
      const done = waiter.until === "acked" ? applied : saved;
      if (done) waiter.resolve();
      return !done;
    });
  }
}

// ---------------------------------------------------------------------------
// React
// ---------------------------------------------------------------------------

export const LiveContext = createContext<LiveSync | null>(null);

export function useLiveSync(): LiveSync | null {
  return useContext(LiveContext);
}

const OFFLINE_STATE: LiveState = {
  status: "offline",
  unsaved: false,
  access: null,
  self: null,
  peers: [],
  closedReason: null,
};
const FALLBACK = createStore<LiveState>(() => OFFLINE_STATE);

/** Live state (status, the others); outside a live editor a constant fallback. */
export function useLive<T>(selector: (state: LiveState) => T): T {
  const live = useContext(LiveContext);
  return useStore(live?.state ?? FALLBACK, selector);
}
